import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FaWallet,
  FaRupeeSign,
  FaArrowUp,
  FaArrowDown,
  FaUser,
  FaCar,
  FaPhone,
  FaCheckCircle,
  FaTimesCircle,
  FaEye,
  FaEyeSlash,
  FaPlus,
  FaSync,
  FaSearch,
  FaFilter,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaExclamationTriangle,
} from "react-icons/fa";
import {
  getVendorWalletSummary,
  addMoneyToDriverWallet,
  updateDriverWalletBalance,
} from "../../features/vendor/vendorWalletSlice";
import { toast } from "react-toastify";

const VendorDriverWalletTable = ({ vendorId }) => {
  const dispatch = useDispatch();
  const { driverWallets, loading, totalCommissionEarned } = useSelector((state) => state.vendorWallet);

  const [showBalances, setShowBalances] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("walletBalance");
  const [sortDirection, setSortDirection] = useState("desc");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showAddMoneyModal, setShowAddMoneyModal] = useState(false);
  const [addMoneyAmount, setAddMoneyAmount] = useState("");
  const [addMoneyDescription, setAddMoneyDescription] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [driversPerPage] = useState(10);

  // Real-time updates tracking
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());

  // Set up real-time listeners for individual driver wallet updates
  useEffect(() => {
    const socket = window.io && window.io();

    if (socket && vendorId) {
      const handleDriverWalletUpdate = (data) => {
       
        if (data.driverId && data.newBalance !== undefined) {
          // Update specific driver's balance in real-time
          dispatch(updateDriverWalletBalance({
            driverId: data.driverId,
            newBalance: data.newBalance
          }));
          
          setLastUpdateTime(Date.now());
          
          // Show toast notification for the update
          const driver = driverWallets.find(dw => dw.driverId === data.driverId);
          if (driver) {
            toast.info(`💰 ${driver.driverName}'s wallet updated: ₹${data.newBalance}`, {
              position: "bottom-right",
              autoClose: 3000,
            });
          }
        }
      };

      // Listen for driver-specific wallet events
      socket.on('driverWalletUpdated', handleDriverWalletUpdate);
      socket.on('tripCompleted', handleDriverWalletUpdate);
      socket.on('commissionProcessed', handleDriverWalletUpdate);

      return () => {
        socket.off('driverWalletUpdated', handleDriverWalletUpdate);
        socket.off('tripCompleted', handleDriverWalletUpdate);
        socket.off('commissionProcessed', handleDriverWalletUpdate);
      };
    }
  }, [vendorId, driverWallets, dispatch]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <FaSort className="w-3 h-3 text-gray-400" />;
    return sortDirection === "asc" ? 
      <FaSortUp className="w-3 h-3 text-blue-500" /> : 
      <FaSortDown className="w-3 h-3 text-blue-500" />;
  };

  const filteredAndSortedDrivers = () => {
    let filtered = driverWallets.filter(driver => {
      const matchesSearch = driver.driverName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           driver.phone?.includes(searchTerm) ||
                           driver.vehicleNumber?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesFilter = filterStatus === "all" ||
                           (filterStatus === "positive" && driver.walletBalance >= 0) ||
                           (filterStatus === "negative" && driver.walletBalance < 0) ||
                           (filterStatus === "active" && driver.isActive) ||
                           (filterStatus === "available" && driver.isAvailable);

      return matchesSearch && matchesFilter;
    });

    // Sort the filtered results
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortDirection === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  };

  const paginatedDrivers = () => {
    const filtered = filteredAndSortedDrivers();
    const startIndex = (currentPage - 1) * driversPerPage;
    return filtered.slice(startIndex, startIndex + driversPerPage);
  };

  const totalPages = () => {
    return Math.ceil(filteredAndSortedDrivers().length / driversPerPage);
  };

  const handleAddMoney = async () => {
    if (!selectedDriver || !addMoneyAmount || parseFloat(addMoneyAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    try {
      await dispatch(addMoneyToDriverWallet({
        driverId: selectedDriver.driverId,
        amount: parseFloat(addMoneyAmount),
        description: addMoneyDescription || `Money added by vendor`
      })).unwrap();

      toast.success(`₹${addMoneyAmount} added to ${selectedDriver.driverName}'s wallet`);
      setShowAddMoneyModal(false);
      setAddMoneyAmount("");
      setAddMoneyDescription("");
      setSelectedDriver(null);

      // Refresh wallet data
      dispatch(getVendorWalletSummary(vendorId));
    } catch (error) {
      toast.error("Failed to add money to driver wallet");
    }
  };

  const getBalanceColor = (balance) => {
    if (balance >= 500) return "text-green-600";
    if (balance >= 0) return "text-blue-600";
    if (balance >= -500) return "text-orange-600";
    return "text-red-600";
  };

  const getBalanceIcon = (balance) => {
    if (balance >= 0) return <FaArrowUp className="w-3 h-3 text-green-500" />;
    return <FaArrowDown className="w-3 h-3 text-red-500" />;
  };

  const getStatusBadge = (driver) => {
    if (!driver.isActive) {
      return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">Inactive</span>;
    }
    if (driver.isAvailable) {
      return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-600">Available</span>;
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-600">Busy</span>;
  };

  if (loading && !driverWallets.length) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <FaWallet className="mr-2 text-purple-600" />
              Driver Wallets
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Manage individual driver wallet balances
              <span className="ml-2 text-xs text-blue-600">
                Last updated: {new Date(lastUpdateTime).toLocaleTimeString()}
              </span>
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="hidden md:flex items-center rounded-lg border border-green-200 bg-green-50 px-3 py-2 mr-2">
              <div className="text-xs text-gray-600 mr-2">Total Commission Balance</div>
              <div className="text-sm font-semibold text-green-700">{showBalances ? formatCurrency(totalCommissionEarned || 0) : '₹****'}</div>
            </div>
            <button
              onClick={() => setShowBalances(!showBalances)}
              className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              {showBalances ? <FaEyeSlash className="mr-1" /> : <FaEye className="mr-1" />}
              {showBalances ? "Hide" : "Show"} Balances
            </button>
            <button
              onClick={() => dispatch(getVendorWalletSummary(vendorId))}
              className="px-3 py-2 text-sm bg-purple-100 text-purple-600 hover:bg-purple-200 rounded-lg transition-colors"
            >
              <FaSync className="mr-1" />
              Refresh
            </button>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="flex flex-col md:flex-row gap-4 mt-4">
          <div className="relative flex-1">
            <FaSearch className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search drivers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Drivers</option>
            <option value="positive">Positive Balance</option>
            <option value="negative">Negative Balance</option>
            <option value="active">Active Drivers</option>
            <option value="available">Available Drivers</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button
                  onClick={() => handleSort("driverName")}
                  className="flex items-center space-x-1 hover:text-gray-700"
                >
                  <span>Driver</span>
                  {getSortIcon("driverName")}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button
                  onClick={() => handleSort("walletBalance")}
                  className="flex items-center space-x-1 hover:text-gray-700"
                >
                  <span>Wallet Balance</span>
                  {getSortIcon("walletBalance")}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button
                  onClick={() => handleSort("totalRides")}
                  className="flex items-center space-x-1 hover:text-gray-700"
                >
                  <span>Total Rides</span>
                  {getSortIcon("totalRides")}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedDrivers().map((driver) => (
              <tr key={driver.driverId} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white font-bold">
                      {driver.driverName?.charAt(0) || 'D'}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {driver.driverName || 'Unknown Driver'}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center">
                        <FaCar className="mr-1 w-3 h-3" />
                        {driver.vehicleNumber || 'N/A'}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 flex items-center">
                    <FaPhone className="mr-1 w-3 h-3 text-gray-400" />
                    {driver.phone || 'N/A'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className={`text-sm font-semibold flex items-center ${getBalanceColor(driver.walletBalance)}`}>
                    {getBalanceIcon(driver.walletBalance)}
                    <span className="ml-1">
                      {showBalances ? formatCurrency(driver.walletBalance) : "₹****"}
                    </span>
                  </div>
                  {driver.walletBalance < 0 && (
                    <div className="text-xs text-red-500 flex items-center mt-1">
                      <FaExclamationTriangle className="mr-1 w-3 h-3" />
                      Commission Due
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 font-medium">
                    {driver.totalRides || 0}
                  </div>
                  <div className="text-xs text-gray-500">
                    Est. ₹{showBalances ? driver.estimatedEarnings || 0 : "****"}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(driver)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => {
                      setSelectedDriver(driver);
                      setShowAddMoneyModal(true);
                    }}
                    className="text-purple-600 hover:text-purple-900 flex items-center"
                  >
                    <FaPlus className="mr-1 w-3 h-3" />
                    Add Money
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages() > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {((currentPage - 1) * driversPerPage) + 1} to{" "}
            {Math.min(currentPage * driversPerPage, filteredAndSortedDrivers().length)} of{" "}
            {filteredAndSortedDrivers().length} drivers
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {[...Array(totalPages())].map((_, i) => (
              <button
                key={i + 1}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 text-sm border rounded ${
                  currentPage === i + 1
                    ? 'bg-purple-600 text-white border-purple-600'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(Math.min(totalPages(), currentPage + 1))}
              disabled={currentPage === totalPages()}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add Money Modal */}
      {showAddMoneyModal && selectedDriver && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full m-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Add Money to {selectedDriver.driverName}'s Wallet
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount (₹)
                  </label>
                  <input
                    type="number"
                    value={addMoneyAmount}
                    onChange={(e) => setAddMoneyAmount(e.target.value)}
                    placeholder="Enter amount"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (Optional)
                  </label>
                  <input
                    type="text"
                    value={addMoneyDescription}
                    onChange={(e) => setAddMoneyDescription(e.target.value)}
                    placeholder="Reason for adding money"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600">
                    Current Balance: <span className="font-medium">{formatCurrency(selectedDriver.walletBalance)}</span>
                  </p>
                  {addMoneyAmount && (
                    <p className="text-sm text-gray-600">
                      New Balance: <span className="font-medium text-green-600">
                        {formatCurrency(selectedDriver.walletBalance + parseFloat(addMoneyAmount || 0))}
                      </span>
                    </p>
                  )}
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowAddMoneyModal(false);
                    setAddMoneyAmount("");
                    setAddMoneyDescription("");
                    setSelectedDriver(null);
                  }}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddMoney}
                  disabled={!addMoneyAmount || parseFloat(addMoneyAmount) <= 0}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Money
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredAndSortedDrivers().length === 0 && (
        <div className="p-12 text-center">
          <FaWallet className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No drivers found</h3>
          <p className="text-gray-600">
            {searchTerm || filterStatus !== "all" 
              ? "Try adjusting your search or filters"
              : "Add drivers to see their wallet information"}
          </p>
        </div>
      )}
    </div>
  );
};

export default VendorDriverWalletTable;
